import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiSave, FiRotateCcw, FiRefreshCw, FiSearch, FiEye, FiEdit2, FiClock } from "react-icons/fi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { DOCUMENT_IDS, DOCUMENT_LABELS, contentSignature, fieldsFor, getPath, resolveContent, setPath, validateContent } from "../../content/cabinetSchema";
import { watchCabinetAccess, loadCabinetContent, saveCabinetContent, getCabinetHistory } from "../../services/cabinetContentService";
import useUnsavedContent from "../../content/useUnsavedContent";
import CabinetPreview from "./CabinetPreview";
import CommercialNewsAdmin from "./main/CommercialNewsAdmin";
import CabinetTilesAdmin from "./users/CabinetTilesAdmin";
import LibraryOffersAdmin from "./LibraryOffersAdmin";
import LibraryCatalogBannerAdmin from "./LibraryCatalogBannerAdmin";
import RoleFormsAdmin from "./RoleFormsAdmin";
import s from "./UserCabinetEditor.module.css";
const RELATED = {
  commercialNews: {
    label: "Комерційні новини",
    component: CommercialNewsAdmin
  },
  businessTiles: {
    label: "Бізнес-плашки та договори",
    component: CabinetTilesAdmin
  },
  libraryOffers: {
    label: "Пропозиції бібліотекам",
    component: LibraryOffersAdmin
  },
  libraryCatalog: {
    label: "Банер каталогу бібліотек",
    component: LibraryCatalogBannerAdmin
  },
  roleForms: {
    label: "Анкети зміни кабінету",
    component: RoleFormsAdmin
  }
};
const QUILL_MODULES = {
  toolbar: [[{
    header: [2, 3, false]
  }], ["bold", "italic", "underline", "strike"], [{
    list: "ordered"
  }, {
    list: "bullet"
  }], [{
    align: []
  }], [{
    color: []
  }, {
    background: []
  }], ["blockquote", "link", "clean"]]
};
const QUILL_FORMATS = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "align", "blockquote", "link", "color", "background"];
const dateLabel = value => value?.toDate ? value.toDate().toLocaleString("uk-UA") : "";
export default function UserCabinetEditor() {
  const root = useRef(null);
  const generation = useRef(0);
  const historyGeneration = useRef(0);
  const [access, setAccess] = useState({
    loading: true,
    allowed: false
  });
  const [baseline, setBaseline] = useState(null);
  const [models, setModels] = useState(null);
  const [activeDoc, setActiveDoc] = useState("userCabinet");
  const [view, setView] = useState("edit");
  const [source, setSource] = useState("content");
  const [group, setGroup] = useState("Основні описи");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [errors, setErrors] = useState({});
  const [history, setHistory] = useState({
    items: [],
    cursor: null,
    hasMore: false
  });
  const [historyBusy, setHistoryBusy] = useState(false);
  const [restoreReasons, setRestoreReasons] = useState({});
  useEffect(() => watchCabinetAccess(setAccess), []);
  const loadedModels = useMemo(() => baseline ? Object.fromEntries(DOCUMENT_IDS.map(id => [id, resolveContent(id, baseline[id] || {})])) : null, [baseline]);
  const dirtyIds = useMemo(() => models && loadedModels ? DOCUMENT_IDS.filter(id => contentSignature(models[id]) !== contentSignature(loadedModels[id])) : [], [models, loadedModels]);
  useUnsavedContent(dirtyIds.length > 0, root);
  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError("");
    try {
      const raw = await loadCabinetContent();
      if (current !== generation.current) return;
      setBaseline(raw);
      setModels(Object.fromEntries(DOCUMENT_IDS.map(id => [id, resolveContent(id, raw[id] || {})])));
      setErrors({});
      setRestoreReasons({});
    } catch (_) {
      if (current === generation.current) setError("Не вдалося завантажити контент. Перевірте доступ і повторіть спробу.");
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (access.allowed) load();else {
      generation.current++;
      setBaseline(null);
      setModels(null);
    }
    return () => {
      generation.current += 1;
    };
  }, [access.allowed, access.user?.uid, load]);
  const fetchHistory = useCallback(async (append = false) => {
    const current = ++historyGeneration.current;
    setHistoryBusy(true);
    setError("");
    try {
      const page = await getCabinetHistory(activeDoc, append ? history.cursor : null);
      if (current === historyGeneration.current) setHistory(previous => ({
        ...page,
        items: append ? [...previous.items, ...page.items] : page.items
      }));
    } catch (_) {
      if (current === historyGeneration.current) setError("Не вдалося завантажити історію змін.");
    } finally {
      if (current === historyGeneration.current) setHistoryBusy(false);
    }
  }, [activeDoc, history.cursor]);
  useEffect(() => {
    historyGeneration.current++;
    if (view !== "history" || !access.allowed) return;
    let alive = true;
    setHistory({
      items: [],
      cursor: null,
      hasMore: false
    });
    setHistoryBusy(true);
    getCabinetHistory(activeDoc).then(page => {
      if (alive) setHistory(page);
    }, () => {
      if (alive) setError("Не вдалося завантажити історію змін.");
    }).finally(() => {
      if (alive) setHistoryBusy(false);
    });
    return () => {
      alive = false;
      historyGeneration.current += 1;
    };
  }, [view, activeDoc, access.allowed]);
  const save = async all => {
    const ids = all ? DOCUMENT_IDS.filter(id => dirtyIds.includes(id) || baseline[id] === null) : [activeDoc];
    if (!ids.length || saving) return;
    const validation = Object.fromEntries(ids.map(id => [id, validateContent(id, models[id])]));
    setErrors(validation);
    setNotice("");
    setError("");
    const invalid = ids.find(id => Object.keys(validation[id]).length);
    if (invalid) {
      setActiveDoc(invalid);
      setView("edit");
      setGroup("Усі поля");
      setSearch("");
      setError("Перевірте позначені поля.");
      return;
    }
    setSaving(true);
    try {
      await saveCabinetContent(Object.fromEntries(ids.map(id => [id, models[id]])), baseline, ids.some(id => restoreReasons[id]) ? "restore" : "edit");
      // Reload only saved documents in the draft, retaining changes in other tabs.
      const raw = await loadCabinetContent();
      setBaseline(previous => ({
        ...previous,
        ...Object.fromEntries(ids.map(id => [id, raw[id]]))
      }));
      setModels(previous => ({
        ...previous,
        ...Object.fromEntries(ids.map(id => [id, resolveContent(id, raw[id] || {})]))
      }));
      setRestoreReasons(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => !ids.includes(id))));
      setNotice("Зміни збережено. Історію оновлено.");
    } catch (e) {
      setError(e.code === "content-conflict" ? e.message : "Не вдалося підтвердити збереження. Ваші зміни залишилися в редакторі. Оновіть дані, щоб перевірити стан.");
    } finally {
      setSaving(false);
    }
  };
  const restore = version => {
    if (!window.confirm("Підставити цю версію в редактор? Публікація відбудеться після збереження.")) return;
    setModels(previous => ({
      ...previous,
      [activeDoc]: resolveContent(activeDoc, version.after || {})
    }));
    setRestoreReasons(previous => ({
      ...previous,
      [activeDoc]: version.id
    }));
    setView("edit");
    setNotice("Версію підставлено. Збережіть для публікації.");
  };
  const changeSource = next => {
    if (source !== "content" && next !== source && !window.confirm("Завершити роботу з поточним редактором?")) return;
    setSource(next);
  };
  const fields = fieldsFor(activeDoc);
  const groups = [...new Set(fields.map(f => f.group || "Основні описи"))];
  const visible = fields.filter(f => (group === "Усі поля" || (f.group || "Основні описи") === group) && `${f.label} ${getPath(models?.[activeDoc], f.path) ?? ""}`.toLocaleLowerCase("uk-UA").includes(search.toLocaleLowerCase("uk-UA")));
  const Related = RELATED[source]?.component;
  return <section className={s.editor} ref={root} aria-label="Контент кабінетів">
    <header className={s.header}><h1>Контент кабінетів</h1><span role="status">{saving ? "Збереження..." : dirtyIds.length ? `Незбережені розділи: ${dirtyIds.length}` : ""}</span></header>
    {access.loading ? <p role="status">Перевірка доступу...</p> : !access.allowed ? <p role="alert">{access.error ? "Не вдалося перевірити доступ до адмінки. Перевірте з’єднання та права Firestore." : "Редагування доступне лише адміністраторам сайту."}</p> : <>
      <div className={s.toolbar}><label>Розділ <select aria-label="Розділ" value={source} disabled={saving} onChange={e => changeSource(e.target.value)}><option value="content">Описи та повідомлення</option>{Object.entries(RELATED).map(([id, item]) => <option key={id} value={id}>{item.label}</option>)}</select></label></div>
      {source !== "content" ? Related && <Related /> : <>
        <div className={s.toolbar}>
          <label>Кабінет <select aria-label="Кабінет" value={activeDoc} disabled={saving} onChange={e => {
              setActiveDoc(e.target.value);
              setGroup("Основні описи");
              setSearch("");
            }}>{DOCUMENT_IDS.map(id => <option key={id} value={id}>{DOCUMENT_LABELS[id]}{dirtyIds.includes(id) ? " *" : ""}</option>)}</select></label>
          <div className={s.modes} role="tablist" aria-label="Перегляд редактора">{[["edit", "Редагування", FiEdit2], ["preview", "Попередній перегляд", FiEye], ["history", "Історія", FiClock]].map(([key, label, Icon]) => <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => setView(key)} disabled={saving}><Icon aria-hidden="true" />{label}</button>)}</div>
          <button type="button" title="Оновити дані" aria-label="Оновити дані" disabled={saving || loading} onClick={() => {
            if (!dirtyIds.length || window.confirm("Скасувати незбережені зміни й оновити дані?")) load();
          }}><FiRefreshCw /></button>
        </div>
        {error && <div role="alert" className={s.error}>{error}</div>}
        {notice && <p role="status" className={s.success}>{notice}</p>}
        {loading ? <p role="status">Завантаження контенту...</p> : !models ? <button type="button" onClick={load}>Повторити завантаження</button> : <>
          {baseline[activeDoc]?._meta && <p className={s.metadata}>Версія {baseline[activeDoc]._meta.revision}. {dateLabel(baseline[activeDoc]._meta.updatedAt)}. Автор: {baseline[activeDoc]._meta.updatedBy}</p>}
          {view === "edit" && <>
            <div className={s.toolbar}><label>Група <select aria-label="Група" value={group} onChange={e => setGroup(e.target.value)}><option>Усі поля</option>{groups.map(label => <option key={label}>{label}</option>)}</select></label><label className={s.search}><FiSearch aria-hidden="true" /><input type="search" aria-label="Пошук тексту" value={search} onChange={e => setSearch(e.target.value)} placeholder="Пошук тексту" /></label></div>
            {errors[activeDoc]?._document && <p role="alert" className={s.error}>{errors[activeDoc]._document}</p>}
            <fieldset className={s.fields} disabled={saving}>{visible.map(field => <ContentField key={`${activeDoc}:${field.path}`} field={field} value={field.path.startsWith("registration.byRole.") && getPath(models[activeDoc], field.path.replace(".byRole.", ".useShared.")) === true ? models[activeDoc].registration.descriptionHtml : getPath(models[activeDoc], field.path)} error={errors[activeDoc]?.[field.path]} disabled={saving || (field.path.startsWith("registration.byRole.") && getPath(models[activeDoc], field.path.replace(".byRole.", ".useShared.")) === true)} onChange={value => {
                setModels(previous => ({
                  ...previous,
                  [activeDoc]: setPath(previous[activeDoc], field.path, value)
                }));
                setNotice("");
              }} />)}</fieldset>
            {!visible.length && <p>Нічого не знайдено.</p>}
          </>}
          {view === "preview" && <CabinetPreview key={activeDoc} models={models} activeDoc={activeDoc} />}
          {view === "history" && <div className={s.history}>
            {historyBusy && <p role="status">Завантаження історії...</p>}
            {!historyBusy && !history.items.length && <p>Історія ще порожня.</p>}
            {history.items.map(item => <article key={item.id} className={s.version}>
              <div className={s.toolbar}><strong>Версія {item.revision}</strong><span>{dateLabel(item.createdAt)}</span><button type="button" disabled={saving} onClick={() => restore(item)}><FiRotateCcw />Відновити</button></div>
              <p className={s.metadata}>Автор: {item.actorUid}. {item.action === "restore" ? "Відновлення" : "Редагування"}</p>
              <details><summary>Змінені поля</summary>{fieldsFor(activeDoc).filter(field => contentSignature(getPath(item.before, field.path)) !== contentSignature(getPath(item.after, field.path))).map(field => <div key={field.path} className={s.diff}><strong>{field.label}</strong><del>{String(getPath(item.before, field.path) ?? "")}</del><ins>{String(getPath(item.after, field.path) ?? "")}</ins></div>)}</details>
              {item.before && <button type="button" disabled={saving} onClick={() => restore({
                ...item,
                after: item.before
              })}>Стан перед цією зміною</button>}
            </article>)}
            {history.hasMore && <button type="button" disabled={historyBusy} onClick={() => fetchHistory(true)}>Показати ще</button>}
          </div>}
          <footer className={s.saveBar}>
            <button type="button" disabled={saving || (!dirtyIds.includes(activeDoc) && baseline[activeDoc] !== null)} onClick={() => save(false)}><FiSave />Зберегти розділ</button>
            <button type="button" className={s.primary} disabled={saving || (!dirtyIds.length && !DOCUMENT_IDS.some(id => baseline[id] === null))} onClick={() => save(true)}><FiSave />Зберегти всі зміни</button>
            <button type="button" disabled={saving || !dirtyIds.includes(activeDoc)} onClick={() => {
              if (window.confirm("Скасувати зміни цього розділу?")) setModels(previous => ({
                ...previous,
                [activeDoc]: loadedModels[activeDoc]
              }));
            }}><FiRotateCcw />Скасувати зміни</button>
          </footer>
        </>}
      </>}
    </>}
  </section>;
}
function ContentField({
  field,
  value,
  error,
  onChange,
  disabled
}) {
  const id = `content-${field.path.replace(/\./g, "-")}`;
  return <div className={s.field}>
    <label id={`${id}-label`} htmlFor={id}>{field.label}</label>
    {field.type === "html" ? <div aria-labelledby={`${id}-label`}><ReactQuill id={id} theme="snow" value={typeof value === "string" ? value : ""} readOnly={disabled} modules={QUILL_MODULES} formats={QUILL_FORMATS} onChange={(html, delta, source) => {
        if (source === "user") onChange(html);
      }} /></div> : field.type === "boolean" ? <input id={id} type="checkbox" checked={value === true} onChange={e => onChange(e.target.checked)} /> : ["textarea", "lines"].includes(field.type) ? <textarea id={id} rows={3} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} value={field.type === "lines" ? Array.isArray(value) ? value.join("\n") : "" : typeof value === "string" ? value : ""} onChange={e => onChange(field.type === "lines" ? e.target.value ? e.target.value.split("\n") : [] : e.target.value)} /> : <input id={id} type={field.type === "email" ? "email" : "text"} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} value={typeof value === "string" ? value : ""} onChange={e => onChange(e.target.value)} />}
    {error && <span id={`${id}-error`} role="alert" className={s.error}>{error}</span>}
  </div>;
}
